import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Popover,
  Button,
  HStack,
  VStack,
  View,
  PopoverHeader,
  PopoverFooter,
  PopoverContent,
  Spacer,
  PopoverBody,
  FormGroup,
  TextInput,
  Textarea,
} from '@packages/systems/spring';
import {
  SITE_ROLES,
  WORKSPACE_ROLES,
} from '@packages/domains/authorization/permissions/catalog';
import {FormSettingsPayload} from '@packages/systems/designer/forms/types';
import {UsersMapType} from '@packages/systems/designer/user';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {EmailDestinationField} from './emailDestinationField';
import {EmailReplyToField} from './emailReplyToField';
import {EmailNotificationFieldLabel} from './emailNotificationFieldLabel';
import {trackEvent} from '@packages/utilities/analytics';
import {useDispatch} from '@packages/systems/designer/flux';
import {formNotificationAdded as notificationAdded} from '@packages/systems/designer/forms/utils';
import NotificationRecord from '@packages/systems/core/records/NotificationRecord';
import {useRecipientVerificationStatus} from './hooks/useRecipientVerificationStatus';
import {useResendRecipientConfirmations} from './hooks/useResendRecipientConfirmations';
import {reportBug} from '@packages/utilities/reporter';

export type EmailOption = {
  label: string;
  value: string;
};

type SiteFormDefaultsData = {
  emailSenderName: string;
  emailDestinations: string[];
  emailSubject: string;
  emailReplyTo: string;
  emailFormTemplate: string;
};

const SUBJECT_TOKENS = [
  {
    label: 'Site Name',
    value: '{{siteName}}',
  },
  {
    label: 'Form Name',
    value: '{{formName}}',
  },
];

const BODY_TOKENS = [
  {
    label: 'Site Name',
    value: '{{siteName}}',
  },
  {
    label: 'Form Name',
    value: '{{formName}}',
  },
  {
    label: 'Form Data',
    value: '{{formData}}',
  },
  {
    label: 'Form Dashboard Url',
    value: '{{formDashboardUrl}}',
  },
];

type EmailNotificationPopoverProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  close: () => void;
  anchorRef: HTMLElement | null;
  isMiniSettingsOpen?: boolean;
  verticalOffset?: number;
  horizontalOffset?: number;
  formSettings: FormSettingsPayload;
  updateFormSettings: (formSettings: FormSettingsPayload) => void;
  siteUsers: UsersMapType;
  formFormElement: ExpressionElement;
  emailFormTargets: string[];
  getSiteFormDefaults?: () => Promise<SiteFormDefaultsData | null>;
};

export const EmailNotificationPopover = ({
  isMiniSettingsOpen = false,
  isOpen,
  close,
  onOpenChange,
  anchorRef,
  verticalOffset,
  horizontalOffset,
  formSettings,
  updateFormSettings,
  siteUsers,
  formFormElement,
  emailFormTargets,
  getSiteFormDefaults,
}: EmailNotificationPopoverProps) => {
  // Set state for the form using form settings, including autocomplete
  const [emailDestinations, setEmailDestinations] = React.useState<string[]>(
    formSettings.emailDestinations || emailFormTargets
  );
  const [emailSenderName, setEmailSenderName] = React.useState<string>(
    formSettings.emailSenderName || ''
  );
  const [emailReplyTo, setEmailReplyTo] = React.useState<string>(
    formSettings.emailReplyTos?.join(',') || ''
  );
  const [emailSubject, setEmailSubject] = React.useState<string>(
    formSettings.emailSubject || ''
  );
  const [emailFormTemplate, setEmailFormTemplate] = React.useState<string>(
    formSettings.emailFormTemplate || ''
  );
  // List of form fields from the form's schema
  const [formFieldTokenOptions, setFormFieldTokenOptions] = React.useState<
    {label: string; value: string; type: string}[]
  >([]);
  const [emailRecipientAdded, setEmailrecipientAdded] = React.useState(false);

  const dispatch = useDispatch();
  // Whether this site enforces recipient email verification (drives the
  // "verification email sent" toast on save).
  const {
    enforced: isVerificationEnforced,
    verifiedEmails,
    canSendConfirmations,
  } = useRecipientVerificationStatus(emailDestinations);
  const {
    mutate: resendConfirmation,
    isLoading: isResendingConfirmation,
    variables: resendingEmail,
  } = useResendRecipientConfirmations();

  const handleResendConfirmation = (email: string) => {
    resendConfirmation(email, {
      onSuccess: ({resent, throttled, unconfirmed}) => {
        let notification;
        if (unconfirmed === 0) {
          // The address was added in this edit and hasn't been applied yet, so
          // the server has nothing to resend.
          notification = NotificationRecord({
            type: 'info',
            message:
              'Apply your changes to send a confirmation email to this recipient.',
            delay: 15000,
          });
        } else if (resent > 0) {
          notification = NotificationRecord({
            type: 'success',
            message: `Confirmation email sent to ${email}.`,
            delay: 15000,
          });
        } else if (throttled > 0) {
          notification = NotificationRecord({
            type: 'info',
            message: `Confirmation email already sent to ${email} in the last 10 minutes. Try again later.`,
            delay: 15000,
          });
        } else {
          notification = NotificationRecord({
            type: 'error',
            message:
              "Couldn't send the confirmation email. Publish your site first, or try again later.",
            delay: 15000,
          });
        }
        dispatch(notificationAdded(notification));
      },
      onError: () => {
        dispatch(
          notificationAdded(
            NotificationRecord({
              type: 'error',
              message:
                "Couldn't send the confirmation email. Try again in a few minutes.",
              delay: 15000,
            })
          )
        );
      },
    });
  };

  // Site-level defaults used as placeholders when form fields are empty
  const [siteDefaults, setSiteDefaults] =
    React.useState<SiteFormDefaultsData | null>(null);

  useEffect(() => {
    if (isOpen && getSiteFormDefaults) {
      getSiteFormDefaults()
        .then(setSiteDefaults)
        .catch((error) => {
          reportBug(
            '[EmailNotificationPopover]',
            'Failed to fetch site form defaults; placeholder text will be unavailable.',
            {
              team: 'developer-platform',
              ticket: 'https://webflow.atlassian.net/browse/DEVPL-3599',
              error,
            }
          );
        });
    }
  }, [isOpen, getSiteFormDefaults]);

  // Cursor position tracking
  const [subjectCursorPosition, setSubjectCursorPosition] = React.useState(0);
  const [bodyCursorPosition, setBodyCursorPosition] = React.useState(0);

  // Utility state
  // isDirty will be used to show user editing in progress warning.
  const [_isDirty, setIsDirty] = useState(false);
  const wrappedSetEmailDestinations = useCallback(
    (destinations: string[]) => {
      setEmailrecipientAdded(true);
      setEmailDestinations(() => [...destinations]);
      setIsDirty(true);
    },
    [setIsDirty]
  );
  const wrappedAddEmailDestination = useCallback(
    (destination: string) => {
      setEmailDestinations((prev) => [...prev, destination]);
      setIsDirty(true);
    },
    [setIsDirty]
  );
  const wrappedSetEmailReplyTo = useCallback(
    (replyToString: string) => {
      setEmailReplyTo(replyToString);
      setIsDirty(true);
    },
    [setIsDirty]
  );

  const updateEmailNotificationSettings = () => {
    if (emailRecipientAdded) {
      trackEvent({
        name: 'Form Notification Recipient Added',
        data: {
          'element id': formFormElement.id,
        },
      });
    }

    const payload = {
      emailDestinations: [...emailDestinations],
      emailSenderName,
      emailReplyTos: emailReplyTo
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email),
      emailSubject,
      emailFormTemplate,
    };
    updateFormSettings(payload);

    // Let the user know a verification email went out to newly-added recipients —
    // they must confirm before they receive form notifications. Only count
    // addresses added in this edit that are neither workspace members nor already
    // verified. This is a best-effort match for the server's skip rules: the
    // client can't see live pending tokens (a re-added address may get no new
    // email) or whether a member's own account email is confirmed (an unconfirmed
    // member does get an email but no toast).
    const normalize = (email: string) => email.trim().toLowerCase();
    const savedEmails = new Set(
      (formSettings.emailDestinations || emailFormTargets).map(normalize)
    );
    const memberEmails = new Set(
      siteUsers
        .toArray()
        .filter(
          ({email, roles}) =>
            email &&
            roles?.workspace &&
            roles.workspace !== WORKSPACE_ROLES.public
        )
        .map(({email}) => normalize(email as string))
    );
    const hasNewUnverifiedRecipient = emailDestinations
      .map(normalize)
      .some(
        (email) =>
          !savedEmails.has(email) &&
          !memberEmails.has(email) &&
          !verifiedEmails.has(email)
      );
    if (isVerificationEnforced && hasNewUnverifiedRecipient) {
      dispatch(
        notificationAdded(
          // Confirmation emails only go out from published sites.
          canSendConfirmations
            ? NotificationRecord({
                type: 'success',
                message:
                  'Confirmation email sent. New recipients need to confirm their email to receive form notifications.',
                delay: 15000,
              })
            : NotificationRecord({
                type: 'info',
                message:
                  'Confirmation email not sent. Publish your site, then resend it to new recipients.',
                delay: 15000,
              })
        )
      );
    }
  };

  const defaultEmailDestinations = useMemo(() => {
    return siteUsers
      .toArray()
      .filter(
        ({roles, email}) => email && roles?.site === SITE_ROLES.project_admin
      )
      .map(({email}) => email) as string[];
  }, [siteUsers]);

  // Reset to defaults does not save or set the state to what's saved on the BE, it just modifies the UI state to match the site's default values. User still has to apply to save.
  const resetToDefaults = useCallback(async () => {
    setIsDirty(true);
    if (getSiteFormDefaults) {
      try {
        const defaults = await getSiteFormDefaults();
        if (defaults) {
          setEmailDestinations(defaults.emailDestinations);
          setEmailSenderName(defaults.emailSenderName);
          setEmailReplyTo(defaults.emailReplyTo);
          setEmailSubject(defaults.emailSubject);
          setEmailFormTemplate(defaults.emailFormTemplate);
          setEmailrecipientAdded(true);
          return;
        }
      } catch {
        // Fall back to default behavior if fetch fails
      }
    }
    setEmailDestinations(defaultEmailDestinations);
    setEmailSenderName('');
    setEmailReplyTo('');
    setEmailSubject('');
    setEmailFormTemplate('');
    setEmailrecipientAdded(true);
  }, [getSiteFormDefaults, defaultEmailDestinations]);

  const resetForm = useCallback(() => {
    setIsDirty(false);
    setEmailrecipientAdded(false);
    setEmailDestinations(formSettings.emailDestinations || emailFormTargets);
    setEmailSenderName(formSettings.emailSenderName || '');
    setEmailReplyTo(formSettings.emailReplyTos?.join(', ') || '');
    setEmailSubject(formSettings.emailSubject || '');
    setEmailFormTemplate(formSettings.emailFormTemplate || '');
  }, [formSettings]);

  useEffect(() => {
    resetForm();
    setIsDirty(false);
    setEmailrecipientAdded(false);
  }, [isOpen, resetForm]);

  const handleCancel = () => {
    resetForm();
    close();
  };

  return (
    <Popover
      minHeight="300px"
      width="400px"
      placement={isMiniSettingsOpen ? 'right' : 'left'}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      anchorRef={anchorRef}
      verticalOffset={verticalOffset}
      horizontalOffset={horizontalOffset}
    >
      <PopoverContent>
        <PopoverHeader title="Email Notifications" hasCloseButton />
        <PopoverBody maxHeight="800px" padding="$2">
          <VStack maxWidth="400px">
            <EmailDestinationField
              emailDestinations={emailDestinations}
              setEmailDestinations={wrappedSetEmailDestinations}
              addEmailDestination={wrappedAddEmailDestination}
              siteUsers={siteUsers}
              onResendConfirmation={handleResendConfirmation}
              resendingEmail={
                isResendingConfirmation ? resendingEmail : undefined
              }
            />
            <FormGroup id="email-notification-sender">
              <EmailNotificationFieldLabel
                htmlFor="email-notification-sender"
                text="Sender Name"
                helpText="Enter the name of the person who is sending the email."
              />
              <TextInput
                value={emailSenderName}
                placeholder={siteDefaults?.emailSenderName}
                onChange={(e) => {
                  setEmailSenderName(e.target.value);
                  setIsDirty(true);
                }}
              ></TextInput>
            </FormGroup>
            <EmailReplyToField
              emailReplyTo={emailReplyTo}
              setEmailReplyTo={wrappedSetEmailReplyTo}
              formFieldTokenOptions={formFieldTokenOptions}
              setFormFieldTokenOptions={setFormFieldTokenOptions}
              formFormElement={formFormElement}
              placeholder={siteDefaults?.emailReplyTo}
            />
            <FormGroup id="email-notification-subject">
              <EmailNotificationFieldLabel
                htmlFor="email-notification-subject"
                text="Subject"
                helpText="Enter the subject of the email notifications."
                tokens={SUBJECT_TOKENS}
                onTokenClick={(value: string) => {
                  const beforeCursor = emailSubject.substring(
                    0,
                    subjectCursorPosition
                  );
                  const afterCursor = emailSubject.substring(
                    subjectCursorPosition
                  );
                  const newSubject = beforeCursor + value + afterCursor;
                  setEmailSubject(newSubject);
                  setIsDirty(true);
                }}
              />
              <TextInput
                value={emailSubject}
                placeholder={siteDefaults?.emailSubject}
                onChange={(e) => {
                  setEmailSubject(e.target.value);
                  setIsDirty(true);
                }}
                onSelect={(e) => {
                  const target = e.target as HTMLInputElement;
                  setSubjectCursorPosition(target.selectionStart || 0);
                }}
              ></TextInput>
            </FormGroup>
            <FormGroup id="email-notification-body">
              <EmailNotificationFieldLabel
                htmlFor="email-notification-body"
                text="Body"
                helpText="Enter the body of the email notifications."
                tokens={BODY_TOKENS}
                onTokenClick={(value: string) => {
                  const beforeCursor = emailFormTemplate.substring(
                    0,
                    bodyCursorPosition
                  );
                  const afterCursor =
                    emailFormTemplate.substring(bodyCursorPosition);
                  const newBody = beforeCursor + value + afterCursor;
                  setEmailFormTemplate(newBody);
                  setIsDirty(true);
                }}
              />
              <Textarea
                value={emailFormTemplate}
                placeholder={siteDefaults?.emailFormTemplate}
                onChange={(e) => {
                  setEmailFormTemplate(e.target.value);
                  setIsDirty(true);
                }}
                onSelect={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  setBodyCursorPosition(target.selectionStart || 0);
                }}
              ></Textarea>
            </FormGroup>
          </VStack>
        </PopoverBody>
        <Spacer />
        <PopoverFooter padding="$2">
          <HStack>
            <Button variant="outline" onClick={resetToDefaults}>
              Reset to defaults
            </Button>
            <Spacer />
            <View>
              <HStack>
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  colorVariant="primary"
                  onClick={updateEmailNotificationSettings}
                >
                  Apply
                </Button>
              </HStack>
            </View>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};
